import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Alert } from "../../components/ui/Alert";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { FormField } from "../../components/ui/FormField";
import { Glossary } from "../../components/ui/Glossary";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { PageHeader } from "../../components/ui/PageHeader";
import { Switch } from "../../components/ui/Switch";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { type Column, Table } from "../../components/ui/Table";
import { useToast } from "../../components/ui/Toast";
import { TypeaheadPicker, type TypeaheadOption } from "../../components/ui/TypeaheadPicker";
import { ApiError } from "../../lib/api";
import {
  type URLGroupMemberRow,
  type URLGroupRow,
  urlGroupsApi,
} from "../../lib/api/network/urlGroups";
import { type URLPoolKind, urlPoolApi } from "../../lib/api/network/urlPool";
import { downloadCsv, rowsToCsv } from "../../lib/csv";
import { formatMessage, t } from "../../lib/i18n";
import { useRecompileNotice } from "../../lib/recompileNotice";
import { useLocale } from "../../lib/useLocale";

interface URLGroupsPageProps {
  embedded?: boolean;
}

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

function inferKind(value: string): URLPoolKind {
  const v = value.trim();
  if (v.includes("/")) return "cidr";
  if (/^[0-9a-f:]+$/i.test(v) && v.includes(":")) return "ipv6";
  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(v)) return "ipv4";
  return "domain";
}

export function URLGroupsPage({ embedded = false }: URLGroupsPageProps) {
  useLocale();
  const qc = useQueryClient();
  const notice = useRecompileNotice();
  const { push: pushToast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const [editRow, setEditRow] = useState<URLGroupRow | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editActive, setEditActive] = useState(true);

  const [pendingDelete, setPendingDelete] = useState<URLGroupRow | null>(null);

  const [params, setParams] = useSearchParams();
  const selectedId = params.get("group");
  const setSelected = (row: URLGroupRow | null) => {
    const next = new URLSearchParams(params);
    if (row) { next.set("group", row.id); next.set("kind", "url"); }
    else next.delete("group");
    setParams(next);
  };

  const list = useQuery({
    queryKey: ["network", "url-groups"],
    queryFn: urlGroupsApi.list,
    refetchOnWindowFocus: "always",
  });
  const selected = list.data?.find((row) => row.id === selectedId) ?? null;

  const create = useMutation({
    mutationFn: () => urlGroupsApi.create({ name, description }),
    onSuccess: (group) => {
      void qc.invalidateQueries({ queryKey: ["network", "url-groups"] });
      void qc.invalidateQueries({ queryKey: ["rules", "url-groups"] });
      setSelected(group);
      setCreateOpen(false);
      setName("");
      setDescription("");
    },
  });

  const editMut = useMutation({
    mutationFn: (vars: { id: string; body: { name?: string; description?: string; is_active?: boolean } }) =>
      urlGroupsApi.patch(vars.id, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["network", "url-groups"] });
      setEditRow(null);
      notice();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => urlGroupsApi.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["network", "url-groups"] });
      setPendingDelete(null);
      setSelected(null);
      notice();
    },
    onError: (err) =>
      pushToast({ tone: "danger", title: t("errors.generic"), body: translateError(err) }),
  });

  function openEdit(row: URLGroupRow) {
    setEditRow(row);
    setEditName(row.name);
    setEditDescription(row.description);
    setEditActive(row.is_active);
  }

  function exportCsv() {
    if (!list.data) return;
    const csv = rowsToCsv(list.data, [
      { header: "name", value: (r) => r.name },
      { header: "description", value: (r) => r.description },
      { header: "is_default", value: (r) => r.is_default },
      { header: "is_active", value: (r) => r.is_active },
      { header: "members_count", value: (r) => r.members_count },
      { header: "rule_usage_count", value: (r) => r.rule_usage_count },
      { header: "created_at", value: (r) => r.created_at },
    ]);
    downloadCsv(`url-groups-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  }

  const columns: Column<URLGroupRow>[] = [
    {
      key: "name",
      header: t("network.urlGroups.column.name"),
      render: (row) => (
        <button
          type="button"
          onClick={() => setSelected(row)}
          style={{
            background: "none",
            border: "none",
            color: "var(--accent)",
            cursor: "pointer",
            padding: 0,
            fontWeight: 500,
            textAlign: "left",
          }}
        >
          <div>{row.name}</div>
          {row.description && (
            <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
              {row.description}
            </div>
          )}
        </button>
      ),
    },
    {
      key: "default",
      header: t("network.urlGroups.column.default"),
      render: (row) =>
        row.is_default ? (
          <span className="badge badge--info">{t("network.urlGroups.defaultBadge")}</span>
        ) : (
          "—"
        ),
    },
    {
      key: "members",
      header: t("network.urlGroups.column.members"),
      render: (row) => <span className="num">{row.members_count}</span>,
      align: "right",
    },
    {
      key: "rule_usage",
      header: t("network.urlGroups.column.usedInRules"),
      render: (row) =>
        row.rule_usage_count > 0 ? (
          <Link
            to={`/policies/rules?destinationGroup=${encodeURIComponent(row.id)}`}
            className="badge badge--info"
            style={{ textDecoration: "none" }}
          >
            {row.rule_usage_count} {t("network.urlGroups.rulesShort")}
          </Link>
        ) : (
          <span className="app-pkg">{t("network.urlGroups.notUsed")}</span>
        ),
      align: "right",
    },
    {
      key: "active",
      header: t("network.urlGroups.column.active"),
      render: (row) =>
        row.is_active ? (
          <span className="badge badge--success">{t("common.yes")}</span>
        ) : (
          <span className="badge">{t("common.no")}</span>
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <Button variant="secondary" size="sm" onClick={() => setSelected(row)}>
            {t("network.urlGroups.action.manage")}
          </Button>
          {!row.is_default && (
            <>
              <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
                {t("network.urlGroups.action.edit")}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setPendingDelete(row)}
              >
                {t("network.urlGroups.action.delete")}
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {!embedded && (
        <PageHeader
          eyebrow={t("network.urlGroups.eyebrow")}
          title={
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              {t("network.urlGroups.title")}
              <Glossary termKey="destinationGroup" />
            </span>
          }
          subtitle={t("network.urlGroups.subtitle")}
          actions={
            <>
              <Button variant="secondary" onClick={exportCsv} disabled={!list.data}>
                {t("network.urlGroups.action.exportCsv")}
              </Button>
              <Button onClick={() => setCreateOpen(true)}>
                {t("network.urlGroups.action.create")}
              </Button>
            </>
          }
        />
      )}

      {embedded && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12, gap: 8 }}>
          <Button variant="secondary" onClick={exportCsv} disabled={!list.data}>
            {t("network.urlGroups.action.exportCsv")}
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            {t("network.urlGroups.action.create")}
          </Button>
        </div>
      )}

      <Card padding="none">
        {list.isLoading && <LoadingState />}
        {list.error && (
          <ErrorState
            titleKey="states.errorTitle"
            message={translateError(list.error)}
          />
        )}
        {list.data && (
          <Table
            rows={list.data}
            columns={columns}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                titleKey="network.urlGroups.emptyTitle"
                bodyKey="network.urlGroups.emptyBody"
              />
            }
          />
        )}
      </Card>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t("network.urlGroups.create.title")}
      >
        <form
          onSubmit={(e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            if (!name.trim()) return;
            create.mutate();
          }}
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          {create.error && <Alert tone="danger">{translateError(create.error)}</Alert>}
          <FormField label={t("network.urlGroups.create.field.name")} required>
            {(id) => (
              <Input
                id={id}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            )}
          </FormField>
          <FormField label={t("network.urlGroups.create.field.description")}>
            {(id) => (
              <Input
                id={id}
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </FormField>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button variant="ghost" type="button" onClick={() => setCreateOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={create.isPending}>
              {t("network.urlGroups.create.submit")}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={editRow !== null}
        onClose={() => setEditRow(null)}
        title={editRow ? formatMessage("network.urlGroups.edit.title", { name: editRow.name }) : ""}
      >
        {editRow && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              editMut.mutate({
                id: editRow.id,
                body: {
                  name: editName,
                  description: editDescription,
                  is_active: editActive,
                },
              });
            }}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            {editMut.error && <Alert tone="danger">{translateError(editMut.error)}</Alert>}
            <FormField label={t("network.urlGroups.create.field.name")} required>
              {(id) => (
                <Input id={id} type="text" value={editName} onChange={(e) => setEditName(e.target.value)} required />
              )}
            </FormField>
            <FormField label={t("network.urlGroups.create.field.description")}>
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              )}
            </FormField>
            <Switch
              checked={editActive}
              onChange={setEditActive}
              label={t("common.active")}
              sub={!editActive ? t("network.urlGroups.notUsed") : undefined}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button variant="ghost" type="button" onClick={() => setEditRow(null)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={editMut.isPending}>
                {t("network.urlGroups.edit.submit")}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={
          pendingDelete
            ? formatMessage("network.urlGroups.delete.title", { name: pendingDelete.name })
            : ""
        }
        body={
          pendingDelete
            ? pendingDelete.rule_usage_count > 0
              ? formatMessage("network.urlGroups.delete.body", {
                  usage: String(pendingDelete.rule_usage_count),
                })
              : t("network.urlGroups.delete.bodyNoUsage")
            : ""
        }
        confirmLabel={t("network.urlGroups.delete.confirm")}
        tone="danger"
        loading={remove.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) remove.mutate(pendingDelete.id);
        }}
      />

      <Modal
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={
          selected
            ? formatMessage("network.urlGroups.manage.title", { name: selected.name })
            : ""
        }
        size="lg"
      >
        {selected && (
          <URLGroupMembersPanel
            key={selected.id}
            group={selected}
            onError={(msg) => pushToast({ tone: "danger", title: t("errors.generic"), body: msg })}
          />
        )}
      </Modal>
    </div>
  );
}

function URLGroupMembersPanel({
  group,
  onError,
}: {
  group: URLGroupRow;
  onError: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const notice = useRecompileNotice();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [poolSearch, setPoolSearch] = useState("");
  const [pickedOptions, setPickedOptions] = useState<TypeaheadOption[]>([]);

  const members = useQuery({
    queryKey: ["network", "url-groups", group.id, "members"],
    queryFn: () => urlGroupsApi.listMembers(group.id),
  });
  const pool = useQuery({
    queryKey: ["network", "url-pool", "member-picker", poolSearch],
    queryFn: () => urlPoolApi.list({ search: poolSearch || undefined, limit: 100 }),
  });

  const invalidateMembers = () =>
    void qc.invalidateQueries({
      queryKey: ["network", "url-groups", group.id, "members"],
    });

  const addMany = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => urlGroupsApi.addMember(group.id, { url_pool: id })),
      );
      const failed = ids.filter((_, index) => results[index].status === "rejected");
      return { ok: ids.length - failed.length, failed };
    },
    onSuccess: (res) => {
      invalidateMembers();
      void qc.invalidateQueries({ queryKey: ["network", "url-groups"] });
      setPicked(new Set(res.failed));
      setPickedOptions((options) => options.filter((option) => res.failed.includes(option.id)));
      if (res.failed.length > 0) onError(formatMessage("operations.domains.partial", { failed: String(res.failed.length), succeeded: String(res.ok) }));
      notice();
    },
  });

  const createAndAdd = useMutation({
    mutationFn: async (value: string) => {
      const created = await urlPoolApi.create({ kind: inferKind(value), value });
      const member = await urlGroupsApi.addMember(group.id, { url_pool: created.id });
      return member;
    },
    onSuccess: () => {
      invalidateMembers();
      void qc.invalidateQueries({ queryKey: ["network", "url-pool"] });
      void qc.invalidateQueries({ queryKey: ["network", "url-groups"] });
      notice();
    },
    onError: (err) => onError(translateError(err)),
  });

  const remove = useMutation({
    mutationFn: (memberId: string) => urlGroupsApi.removeMember(group.id, memberId),
    onSuccess: () => {
      invalidateMembers();
      void qc.invalidateQueries({ queryKey: ["network", "url-groups"] });
      notice();
    },
    onError: (err) => onError(translateError(err)),
  });

  const existingPoolIds = useMemo(() => {
    const set = new Set<string>();
    for (const m of members.data ?? []) set.add(m.url_pool);
    return set;
  }, [members.data]);

  const options: TypeaheadOption[] = useMemo(() => {
    const candidates = (pool.data ?? []).map((p) => ({
      id: p.id,
      label: p.value,
      meta: p.kind.toUpperCase(),
      disabled: existingPoolIds.has(p.id),
      disabledReason: existingPoolIds.has(p.id)
        ? t("network.urlGroups.member.add.placeholder")
        : undefined,
    }));
    return Array.from(new Map([...pickedOptions, ...candidates].map((option) => [option.id, option])).values());
  }, [pool.data, existingPoolIds, pickedOptions]);

  const columns: Column<URLGroupMemberRow>[] = [
    {
      key: "value",
      header: t("network.urlGroups.member.column.value"),
      render: (row) => (
        <span className="mono" style={{ fontSize: 12 }}>
          {row.url_pool_value}
        </span>
      ),
    },
    {
      key: "vuln",
      header: t("network.urlGroups.member.column.vuln"),
      render: (row) => (
        <span className={`badge badge--${row.vuln_status === "malicious" ? "danger" : row.vuln_status === "clean" ? "success" : "info"}`}>
          {t(`network.urlPool.vulnStatus.${row.vuln_status}`)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => remove.mutate(row.id)}
          loading={remove.isPending}
        >
          {t("network.urlGroups.member.action.remove")}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <FormField label={t("network.urlGroups.member.add.urlPool")}>
        {() => (
          <TypeaheadPicker
            options={options}
            selectedIds={picked}
            onChange={(ids) => {
              setPicked(ids);
              setPickedOptions(options.filter((option) => ids.has(option.id)));
            }}
            query={poolSearch}
            onQueryChange={setPoolSearch}
            loading={pool.isFetching || createAndAdd.isPending}
            placeholder={t("network.urlGroups.member.add.placeholder")}
            onCreate={createAndAdd.isPending ? undefined : (value) => createAndAdd.mutate(value)}
            createLabel={(q) =>
              formatMessage("network.urlGroups.member.add.addNew", { query: q })
            }
          />
        )}
      </FormField>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button
          onClick={() => addMany.mutate(Array.from(picked))}
          loading={addMany.isPending}
          disabled={picked.size === 0 || createAndAdd.isPending || members.isLoading}
        >
          {picked.size > 1
            ? formatMessage("network.urlGroups.member.add.submitMany", {
                count: String(picked.size),
              })
            : t("network.urlGroups.member.add.submit")}
        </Button>
      </div>

      {pool.error && <Alert tone="danger">{translateError(pool.error)}</Alert>}
      <Alert tone="info">{t("network.urlGroups.member.modeHint")}</Alert>
      <Link to={`/rules/new?destinationGroup=${encodeURIComponent(group.id)}`}>{t("operations.domains.createPolicy")}</Link>

      {members.isLoading && <LoadingState />}
      {members.error && (
        <ErrorState
          titleKey="states.errorTitle"
          message={translateError(members.error)}
        />
      )}
      {members.data && (
        <Table
          rows={members.data}
          columns={columns}
          rowKey={(row) => row.id}
          empty={
            <EmptyState
              titleKey="network.urlGroups.member.emptyTitle"
              bodyKey="network.urlGroups.member.emptyBody"
            />
          }
        />
      )}
    </div>
  );
}
